import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { logEvent } from '../../utils/eventLog';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { APP_LINK_STATE, NAV_ROUTES } from '../Home/navRoutes';
import { AM, AM_TEXT } from '../Dashboard/assignmentMonitoringTokens';
import { TopBar, type Crumb } from '../IndividualPlan/TopBar';
import {
  buildRecordCsv,
  downloadCsv,
  monthChip,
  monthName,
  plural,
  recordSubline,
  requirementPhrase,
  schoolsLabel,
  shortSchool,
} from '../Dashboard/assignmentMonitoring';
import {
  AssignmentMonitoringError,
  fetchAssignmentMonitoringRecord,
  type MonthRecord,
  type StudentRow,
  type TeacherRow,
} from '../Dashboard/assignmentMonitoringApi';
import { MetBar, Pill, Toast } from '../Dashboard/components/AssignmentMonitoringUi';
import { assignmentRecordRoute } from '../Dashboard/components/AssignmentMonitoringCard';
import {
  COL,
  Collapsed,
  EmptyRow,
  ErrorCard,
  LinkRow,
  LoadingCard,
  MetPill,
  MonthSelect,
  NameCell,
  OutlineButton,
  RecordCard,
  RequirementCard,
  ShortfallCells,
  SkippedCard,
  StudentSection,
  TableHead,
} from './recordParts';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export type LoadRecord = (month: string) => Promise<MonthRecord>;

const COPY_FAILED = 'Couldn’t copy the email address. Copy it from the page instead.';

type Loaded = { kind: 'loading' } | { kind: 'ready'; record: MonthRecord } | { kind: 'error' };

// Unknown months (no record) go back to the dashboard; outages show the
// record's error card with the header still in place.
function useMonthRecord(month: string, load: LoadRecord): Loaded {
  const [settled, setSettled] = useState<{ month: string; loaded: Loaded } | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    load(month)
      .then((record) => {
        if (active) setSettled({ month, loaded: { kind: 'ready', record } });
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof AssignmentMonitoringError && err.status === 404) {
          navigate(NAV_ROUTES.dashboard, { replace: true, state: APP_LINK_STATE });
          return;
        }
        console.warn('assignment monitoring record unavailable', err);
        setSettled({ month, loaded: { kind: 'error' } });
      });
    return () => {
      active = false;
    };
  }, [month, load, navigate]);
  return settled && settled.month === month ? settled.loaded : { kind: 'loading' };
}

export function schoolRoute(month: string, schoolId: string): string {
  return `${assignmentRecordRoute(month)}/school/${encodeURIComponent(schoolId)}`;
}

export function teacherRoute(month: string, schoolId: string, teacherId: string): string {
  return `${schoolRoute(month, schoolId)}/teacher/${encodeURIComponent(teacherId)}`;
}

function Shell({ crumbs, title, chip, sub, actions, children }: { crumbs: Crumb[]; title: string; chip?: ReactNode; sub: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: AM.background }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={crumbs} />
        <Box data-testid="am-content" sx={{ p: '24px', maxWidth: PAGE_MAX_WIDTH + 48, width: '100%', mx: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Typography component="h1" sx={{ fontSize: '32px', lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.5px', color: AM.ink }}>
                    {title}
                  </Typography>
                  {chip}
                </Box>
                <Typography data-testid="record-subline" sx={{ ...AM_TEXT.body1, color: AM.muted, mt: '4px' }}>
                  {sub}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>{actions}</Box>
            </Box>
          </Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function monthLike(record: MonthRecord) {
  return { month: record.month, phase: record.phase, closeDate: record.closeDate, skipped: record.skipped, window: record.window };
}

function recordChip(record: MonthRecord) {
  const chip = monthChip(monthLike(record));
  return <Pill label={chip.label} tone={chip.tone} />;
}

function districtCrumb(month: string, link: boolean): Crumb {
  return { label: `Assignment monitoring · ${monthName(month)}`, to: link ? assignmentRecordRoute(month) : undefined };
}

const DASHBOARD_CRUMB: Crumb = { label: 'Dashboard', to: NAV_ROUTES.dashboard };

function pendingSubline(loaded: Loaded, month: string, prefix?: string): string {
  if (loaded.kind === 'ready') return recordSubline(monthLike(loaded.record), loaded.record.requirement, prefix);
  return prefix ? `${prefix}` : monthName(month);
}

export function AssignmentMonitoringMonthPage({ load = fetchAssignmentMonitoringRecord }: { load?: LoadRecord }) {
  const { month = '' } = useParams();
  const loaded = useMonthRecord(month, load);
  const record = loaded.kind === 'ready' ? loaded.record : null;
  const download = () => {
    if (record) downloadCsv(`assignment-monitoring-${record.month}.csv`, buildRecordCsv(record));
  };
  const actions = record ? (
    <>
      <MonthSelect current={record.month} months={record.months} hrefFor={assignmentRecordRoute} />
      {record.phase === 'final' && !record.skipped ? (
        <OutlineButton label="Download record" icon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} onClick={download} />
      ) : null}
    </>
  ) : null;
  let body: ReactNode;
  if (loaded.kind === 'loading') body = <LoadingCard month={month} />;
  else if (loaded.kind === 'error') body = <ErrorCard month={month} />;
  else if (record!.skipped && record!.window) body = <SkippedCard month={month} window={record!.window} />;
  else {
    const r = record!;
    body = (
      <>
        <RequirementCard group={r.district} req={r.requirement} />
        <RecordCard title="Schools" sub={`${r.district.teachersMet} of ${plural(r.district.teachers, 'teacher')} met`} right="Fewest teachers met first" testId="schools-card">
          <TableHead>
            <Box sx={COL.name}>School</Box>
            <Box sx={{ flex: '1 1 0' }}>Teachers met · Students met</Box>
            <Box className="num">Not assigned</Box>
            <Box className="num">Not submitted</Box>
            <Box className="num">Not scored</Box>
            <Box sx={COL.chevron} />
          </TableHead>
          {(r.schools ?? []).map((s) => (
            <LinkRow key={s.id} to={schoolRoute(r.month, s.id)} onClick={() => void logEvent('progress_monitoring.school_opened.v1').catch(() => {})}>
              <NameCell name={shortSchool(s.name)} small={`${plural(s.teachers, 'teacher')} · ${plural(s.students, 'student')}`} />
              <Box sx={COL.grow}>
                <Typography sx={{ ...AM_TEXT.body2Bold, color: AM.ink }}>{s.teachersMet} of {plural(s.teachers, 'teacher')} met</Typography>
                <MetBar met={s.teachersMet} notMet={s.teachers - s.teachersMet} />
                <Typography sx={{ ...AM_TEXT.body2Semi, color: AM.muted, pt: '4px' }}>{s.met} of {plural(s.students, 'student')} met</Typography>
                <MetBar thin met={s.met} notMet={s.students - s.met} />
              </Box>
              <ShortfallCells group={s} />
            </LinkRow>
          ))}
        </RecordCard>
      </>
    );
  }
  return (
    <Shell crumbs={[DASHBOARD_CRUMB, districtCrumb(month, false)]} title={monthName(month)} chip={record ? recordChip(record) : null} sub={pendingSubline(loaded, month)} actions={actions}>
      {body}
    </Shell>
  );
}

function TeacherRows({ record, schoolId, teachers }: { record: MonthRecord; schoolId: string; teachers: TeacherRow[] }) {
  const [open, setOpen] = useState(false);
  const schoolName = new Map((record.schools ?? []).map((s) => [s.id, s.name]));
  const rows = teachers
    .map((t) => ({ t, here: t.schools.find((s) => s.schoolId === schoolId)! }))
    .sort((a, b) => b.here.students - b.here.met - (a.here.students - a.here.met) || a.t.name.localeCompare(b.t.name));
  const notMet = rows.filter((r) => !r.here.metAll);
  const met = rows.filter((r) => r.here.metAll);
  const row = ({ t, here }: (typeof rows)[number]) => {
    const others = t.schools.filter((s) => s.schoolId !== schoolId).map((s) => shortSchool(schoolName.get(s.schoolId) ?? s.schoolId));
    return (
      <LinkRow key={t.id} to={teacherRoute(record.month, schoolId, t.id)} onClick={() => void logEvent('progress_monitoring.teacher_opened.v1').catch(() => {})}>
        <NameCell name={t.name} small={plural(here.students, 'student')} link={others.length ? `Also at ${others.join(' and ')}` : undefined} />
        <Box sx={COL.grow}>
          <Typography sx={{ ...AM_TEXT.body2Bold, color: AM.ink }}>{here.met} of {here.students} met</Typography>
          <MetBar met={here.met} notMet={here.students - here.met} />
        </Box>
        <ShortfallCells group={here} />
      </LinkRow>
    );
  };
  if (rows.length === 0) {
    return <EmptyRow>No teachers have rostered students at this school yet.</EmptyRow>;
  }
  return (
    <>
      {notMet.map(row)}
      {notMet.length === 0 ? <EmptyRow>Every teacher met the requirement.</EmptyRow> : null}
      {met.length ? (
        <>
          <Collapsed label={`${plural(met.length, 'teacher')} met`} open={open} onToggle={() => setOpen((o) => !o)} />
          {open ? met.map(row) : null}
        </>
      ) : null}
    </>
  );
}

export function AssignmentMonitoringSchoolPage({ load = fetchAssignmentMonitoringRecord }: { load?: LoadRecord }) {
  const { month = '', schoolId = '' } = useParams();
  const loaded = useMonthRecord(month, load);
  const record = loaded.kind === 'ready' ? loaded.record : null;
  const school = record?.schools?.find((s) => s.id === schoolId) ?? null;
  const title = school ? shortSchool(school.name) : '';
  // A month from before the school existed has no record for it: the
  // district record for that month is the right place to land.
  const navigate = useNavigate();
  const unknownSchool = Boolean(record) && !school;
  useEffect(() => {
    if (unknownSchool) navigate(assignmentRecordRoute(month), { replace: true });
  }, [unknownSchool, month, navigate]);
  const actions = record ? <MonthSelect current={record.month} months={record.months} hrefFor={(m) => schoolRoute(m, schoolId)} /> : null;
  let body: ReactNode;
  if (loaded.kind === 'loading') body = <LoadingCard month={month} />;
  else if (loaded.kind === 'error' || !record) body = <ErrorCard month={month} />;
  else if (record.skipped && record.window) body = <SkippedCard month={month} window={record.window} />;
  else if (!school) body = <ErrorCard month={month} />;
  else {
    const teachers = (record.teachers ?? []).filter((t) => t.schools.some((s) => s.schoolId === schoolId));
    body = (
      <>
        <RequirementCard group={school} req={record.requirement} />
        <RecordCard title="Teachers" sub={`${school.teachersMet} of ${school.teachers} met`} right="Furthest from met first" testId="teachers-card">
          <TableHead>
            <Box sx={COL.name}>Teacher</Box>
            <Box sx={{ flex: '1 1 0' }}>Students met</Box>
            <Box className="num">Not assigned</Box>
            <Box className="num">Not submitted</Box>
            <Box className="num">Not scored</Box>
            <Box sx={COL.chevron} />
          </TableHead>
          <TeacherRows record={record} schoolId={schoolId} teachers={teachers} />
        </RecordCard>
      </>
    );
  }
  return (
    <Shell crumbs={[DASHBOARD_CRUMB, districtCrumb(month, true), { label: title }]} title={title} chip={record ? recordChip(record) : null} sub={pendingSubline(loaded, month, monthName(month))} actions={actions}>
      {body}
    </Shell>
  );
}

function studentsOf(record: MonthRecord, teacherId: string, schoolId?: string): StudentRow[] {
  return (record.students ?? []).filter((s) => s.teacherIds.includes(teacherId) && (!schoolId || s.schoolId === schoolId));
}

export function AssignmentMonitoringTeacherPage({ load = fetchAssignmentMonitoringRecord }: { load?: LoadRecord }) {
  const { month = '', schoolId = '', teacherId = '' } = useParams();
  const loaded = useMonthRecord(month, load);
  const record = loaded.kind === 'ready' ? loaded.record : null;
  const teacher = record?.teachers?.find((t) => t.id === teacherId && t.schools.some((s) => s.schoolId === schoolId)) ?? null;
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);
  const navigate = useNavigate();
  // A skipped month, or a teacher with no students at the routed school this
  // month, has nothing to show here: the school record is the right place.
  const backToSchool = Boolean(record) && (Boolean(record?.skipped) || !teacher);
  useEffect(() => {
    if (backToSchool) navigate(schoolRoute(month, schoolId), { replace: true });
  }, [backToSchool, month, schoolId, navigate]);
  const schoolName = new Map((record?.schools ?? []).map((s) => [s.id, s.name]));
  const fromSchool = schoolName.get(schoolId);
  const copyEmail = () => {
    if (!teacher) return;
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      setToast(COPY_FAILED);
      return;
    }
    clipboard.writeText(teacher.email).then(
      () => setToast(`Copied ${teacher.email}`),
      () => setToast(COPY_FAILED),
    );
  };
  const actions = teacher ? <OutlineButton label="Copy email" icon={<ContentCopyIcon sx={{ fontSize: 16 }} />} onClick={copyEmail} /> : null;
  const schools = teacher ? teacher.schools.map((s) => schoolName.get(s.schoolId) ?? s.schoolId) : [];
  const sub = record && teacher ? `${schoolsLabel(schools)} · ${monthName(month)} · ${requirementPhrase(record.requirement)}` : monthName(month);
  let body: ReactNode;
  if (loaded.kind === 'loading') body = <LoadingCard month={month} />;
  else if (loaded.kind === 'error' || !record || !teacher) body = <ErrorCard month={month} />;
  else {
    const all = studentsOf(record, teacher.id);
    const multi = teacher.schools.length > 1;
    body = (
      <>
        <RequirementCard group={teacher.group} req={record.requirement} />
        <RecordCard title="Students" sub={`${teacher.met} of ${teacher.students} met`} chip={multi ? <MetPill met={teacher.metAll} /> : null} right="Furthest from met first" testId="students-card">
          <TableHead>
            <Box sx={COL.name}>Student</Box>
            <Box sx={{ flex: '1 1 0' }}>Status</Box>
            <Box className="num">Assigned</Box>
            <Box className="num">Submitted</Box>
            <Box className="num">Scored</Box>
            <Box sx={COL.chevron} />
          </TableHead>
          {multi
            ? teacher.schools.map((s) => (
                <Box key={s.schoolId} data-testid="school-group">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', mt: '16px', p: '12px 16px', borderRadius: '8px', bgcolor: AM.background }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', ...AM_TEXT.body1Bold, color: AM.ink }}>
                      {shortSchool(schoolName.get(s.schoolId) ?? s.schoolId)}
                      <Typography component="small" sx={{ ...AM_TEXT.body2Semi, color: AM.muted }}>
                        {plural(s.students, 'student')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', ...AM_TEXT.body1Semi, color: AM.ink }}>
                      {s.met} of {s.students} met <MetPill met={s.metAll} />
                    </Box>
                  </Box>
                  <StudentSection rows={studentsOf(record, teacher.id, s.schoolId)} req={record.requirement} inset />
                </Box>
              ))
            : <StudentSection rows={all} req={record.requirement} />}
        </RecordCard>
      </>
    );
  }
  const crumbs: Crumb[] = [DASHBOARD_CRUMB, districtCrumb(month, true)];
  if (fromSchool) crumbs.push({ label: shortSchool(fromSchool), to: schoolRoute(month, schoolId) });
  crumbs.push({ label: teacher?.name ?? '' });
  return (
    <>
      <Shell crumbs={crumbs} title={teacher?.name ?? ''} chip={record ? recordChip(record) : null} sub={sub} actions={actions}>
        {body}
      </Shell>
      <Toast message={toast} onDone={clearToast} />
    </>
  );
}
